import merge from 'deepmerge';
import CopyFormDefaultValue from './copy-form-default-value';
import * as helpers from '../../helpers';

export default class CopyForm {
    constructor(el, copyButton, formEl, options = {}) {
        this.defaultOptions = {
            classList: {
                removeButton: 'js-remove-button',
                blockToCopy: 'js-block-to-copy',
                copiedBlockModifier: '-is-copy',
                index: 'js-index',
            },
            submitLogic: true,
            isInsideAnotherCopy: false,
            onInit: () => null,
            afterChange: () => null,
            beforeCopy: () => null,
            afterCopy: () => null,
            afterDelete: () => null,
            onHTMLCopy: htmlString => htmlString,
        };

        // Events binding
        this.$handleCopyButtonClick = this._handleCopyButtonClick.bind(this);
        this.$handleRemoveButtonClick = this._handleRemoveButtonClick.bind(this);
        this.$handleFormSubmit = this._handleFormSubmit.bind(this);

        this.options = merge(this.defaultOptions, options);
        this.el = el;
        this.formEl = formEl;
        this.copyButton = copyButton;

        this.maxElements = +el.getAttribute('data-max-elements') || Infinity;

        this.init();
    }

    init() {
        this._getDOMElements();
        this._initEvents();
        this._manageAllBlocksIndex();

        if (this.options.submitLogic) {
            this._setDefaultValue();
        }

        this.options.onInit();
    }

    manageCopyButtonsEnabled() {
        if (this.maxElements === this.currentElementsCount) {
            this.copyButton.disable();
            helpers.addClass(this.copyButton.buttonEl, 'disabled-by-main-copy');
        } else {
            this.copyButton.enable();
            helpers.removeClass(this.copyButton.buttonEl, 'disabled-by-main-copy');
        }
    }

    _manageAllBlocksIndex() {
        const { blockToCopy: blockToCopyClass } = this.options.classList;
        const blocksToCopy = [...this.el.querySelectorAll(`.${blockToCopyClass}`)];

        blocksToCopy.forEach((el, index) => {
            this._manageHTMLIndex(el, index + 1);
        });
    }

    _manageHTMLIndex(blockToManage, index) {
        const { index: indexClass } = this.options.classList;
        const indexSpanArray = [...blockToManage.querySelectorAll(`.${indexClass}`)];
        
        indexSpanArray.forEach((indexEl) => {
            indexEl.innerHTML = index;
        });
    }

    _setDefaultValue() {
        const inputHiddenValue = this.inputHidden.value;

        if (!inputHiddenValue) {
            return;
        }

        const inputHiddenValueJSON = JSON.parse(inputHiddenValue);

        this.copyFormDefaultValue = new CopyFormDefaultValue(inputHiddenValueJSON, this.el, this.copyButton.buttonEl);
    }

    _getDOMElements() {
        const { removeButton, blockToCopy, copiedBlockModifier } = this.options.classList;
        const inputHiddenSelector = this.el.getAttribute('data-input-hidden-selector');

        this.blockToCopy = this.el.querySelector(`.${blockToCopy}`);
        this.currentElementsCount = [...this.el.querySelectorAll(`.${copiedBlockModifier}`)].length + 1;
        this.removeButtonsArray = [...this.el.querySelectorAll(`.${removeButton}`)];

        if (this.options.submitLogic) {
            this.inputHidden = document.querySelector(inputHiddenSelector);
        }
    }

    _initEvents() {
        this.copyButton.buttonEl.addEventListener('click', this.$handleCopyButtonClick);

        this.removeButtonsArray.forEach((btn) => {
            btn.addEventListener('click', this.$handleRemoveButtonClick);
        });

        if (this.options.submitLogic) {
            this.formEl.addEventListener('submit', this.$handleFormSubmit);
        }
    }

    _removeEvents() {
        this.copyButton.buttonEl.removeEventListener('click', this.$handleCopyButtonClick);

        this.removeButtonsArray.forEach((btn) => {
            btn.removeEventListener('click', this.$handleRemoveButtonClick);
        });

        if (this.options.submitLogic) {
            this.formEl.removeEventListener('submit', this.$handleFormSubmit);
        }
    }

    copyBlock() {
        const { copiedBlockModifier } = this.options.classList;

        if (this.maxElements === this.currentElementsCount) {
            return;
        }

        this.options.beforeCopy();

        this.currentElementsCount += 1;

        const newBlock = document.createElement(this.blockToCopy.tagName);

        newBlock.classList = this.blockToCopy.classList;
        newBlock.setAttribute('id', `${this.blockToCopy.getAttribute('id')}-${this.currentElementsCount}`);
        helpers.addClass(newBlock, copiedBlockModifier);
        newBlock.innerHTML = this.options.onHTMLCopy(this.blockToCopy.innerHTML);

        this._manageHTMLIndex(newBlock, this.currentElementsCount);
        this.manageCopyButtonsEnabled();

        this.el.appendChild(newBlock);

        this.options.afterCopy();
        this.options.afterChange();
        this.update();
    }

    _handleCopyButtonClick() {
        this.copyBlock();
    }

    _handleRemoveButtonClick(e) {
        const { copiedBlockModifier } = this.options.classList;

        this.currentElementsCount -= 1;

        const blockToDelete = helpers.findAncestor(e.target, copiedBlockModifier);
        this.el.removeChild(blockToDelete);

        this.update();
        this._manageAllBlocksIndex();
        this.manageCopyButtonsEnabled();

        this.options.afterDelete();
        this.options.afterChange();
    }

    /**
     * Set values from dynamic form to hidden input
     * @param {Event} _e 
     */
    _handleFormSubmit(_e) {
        const { blockToCopy: blockToCopyClass } = this.options.classList;
        const blocksToCopy = [...this.el.querySelectorAll(`.${blockToCopyClass}`)];
        const allFieldsValue = [];

        blocksToCopy.forEach((blockToCopy) => {
            const defaultFieldsWithValue = [...blockToCopy.querySelectorAll('input, textarea, select')];
            const fieldsValue = {};
            const dist = [];
            let distObj = {};

            defaultFieldsWithValue.forEach((field) => {
                const isFieldCanBeChecked = field.getAttribute('type') === 'checkbox' || field.getAttribute('type') === 'radio';
                const fieldName = field.getAttribute('name');

                if ((field.value && !isFieldCanBeChecked) || (isFieldCanBeChecked && field.checked)) {
                    if (fieldName === 'discipline' || fieldName === 'commune' || fieldName === 'club' || fieldName === 'federation') {
                        if (Object.keys(distObj).length !== 3) {
                            distObj[fieldName] = field.value;
                        }

                        if (Object.keys(distObj).length === 3) {
                            dist.push(distObj);
                            distObj = {};
                        }
                    } else {
                        fieldsValue[fieldName] = field.value;
                    }
                }
            });

            fieldsValue.dist = dist;

            allFieldsValue.push(fieldsValue);
        });

        this.inputHidden.value = JSON.stringify(allFieldsValue);
    }

    update() {
        const { removeButton } = this.options.classList;

        this._removeEvents();
        this.removeButtonsArray = [...this.el.querySelectorAll(`.${removeButton}`)];
        this._initEvents();
    }

    destroy() {
        this._removeEvents();
    }
}
